import type { Project } from '@shared/models/project.model';
import {
  addDuplicateVersionToProject,
  addVersionToProject,
} from '../../project.helpers';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';

export type VersionAddChange = ProjectChangeMap[ProjectChangeType.VersionAdd];

/**
 * Apply a single {@link ProjectChangeType.VersionAdd} change by delegating to
 * {@link addVersionToProject} so name dedup and proposal id rotation stay in one place.
 *
 * The root id always comes from {@link VersionAddChange.versionId} while children get fresh
 * ids — a duplicate must never reuse the source version's element ids.
 *
 * Passes the factory's original version id as `sourceRootId` so cloned resultsRecord rows
 * remap onto the new id even when leftover keys exist (skip-enrichment baseline-from-proposal).
 */
export const applyVersionAddChange = (
  project: Project,
  change: VersionAddChange,
): Project => {
  const regenerateIds = {
    preserveRootElementIds: true,
    ...(change.factory?.id ? { sourceRootId: change.factory.id } : {}),
  };
  const factory = { ...change.factory, id: change.versionId };

  return change.duplicateNameBase
    ? addDuplicateVersionToProject(
        project,
        regenerateIds,
        change.duplicateNameBase,
        factory,
      )
    : addVersionToProject(project, regenerateIds, factory);
};
